import { useMediaQuery, useTheme } from '@presov.sk/presov-ui-react';
import React from 'react';

type ResponsiveContextType = {
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;

  isNotSm: boolean;
  isNotMd: boolean;
  isNotLg: boolean;
  isNotXl: boolean;

  isPrint: boolean;
};

const ResponsiveContext = React.createContext<
  ResponsiveContextType | undefined
>(undefined);

type Props = React.PropsWithChildren;

const ResponsiveProvider: React.FC<Props> = ({ children }) => {
  const theme = useTheme();
  const isPrint = useMediaQuery('print');

  // Up breakpoints (greater than or equal to)
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));

  // Down breakpoints (less than) - opposite of up
  const isNotSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isNotMd = useMediaQuery(theme.breakpoints.down('md'));
  const isNotLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isNotXl = useMediaQuery(theme.breakpoints.down('xl'));

  // Force desktop-like breakpoints in print mode
  if (isPrint) {
    return (
      <ResponsiveContext.Provider
        value={{
          isSm: true,
          isMd: true,
          isLg: true,
          isXl: true,
          isNotSm: false,
          isNotMd: false,
          isNotLg: false,
          isNotXl: false,
          isPrint: true,
        }}
      >
        {children}
      </ResponsiveContext.Provider>
    );
  }

  return (
    <ResponsiveContext.Provider
      value={{
        isSm,
        isMd,
        isLg,
        isXl,
        isNotSm,
        isNotMd,
        isNotLg,
        isNotXl,
        isPrint: false,
      }}
    >
      {children}
    </ResponsiveContext.Provider>
  );
};

export const useResponsive = (): ResponsiveContextType => {
  const context = React.useContext(ResponsiveContext);
  if (context === undefined) {
    throw new Error('useResponsive must be used within a ResponsiveProvider');
  }
  return context;
};

export default ResponsiveProvider;
